import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, money, fmtDate, today, toPaise, fromPaise, ApiError } from '../api'
import { Page } from '../App'
import { Card, Badge, Alert, Spinner, Empty, Modal, Field } from '../components/ui'
import type { Client } from './Clients'

interface Profile {
  id: string
  client_id: string
  name: string
  template_json: string
  frequency: string
  interval_n: number
  day_of_month: number | null
  lead_days: number
  payment_terms_days: number
  start_date: string
  end_date: string | null
  max_occurrences: number | null
  occurrences_done: number
  next_run_on: string
  mode: string
  approval_threshold: number | null
  status: string
  consecutive_failures: number
  last_error: string | null
  client_legal_name: string
  client_display_name: string | null
  upcoming: string[]
}

const FREQ_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-yearly',
  yearly: 'Yearly',
  custom_days: 'Every N days',
}

export default function Recurring() {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()

  const load = async () => {
    setLoading(true)
    try {
      setProfiles((await api.get<{ profiles: Profile[] }>('/recurring')).profiles)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  return (
    <Page
      title="Recurring invoices"
      subtitle="Generated at 09:00 IST on their due day"
      actions={
        <button className="btn primary" onClick={() => setCreating(true)}>
          + New recurring profile
        </button>
      }
    >
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>

      {loading ? (
        <Spinner />
      ) : profiles.length === 0 ? (
        <Card>
          <Empty
            icon="↻"
            title="No recurring profiles"
            hint="Ideal for monthly retainers. Set one up and the invoice raises itself."
          />
        </Card>
      ) : (
        <div className="grid" style={{ gap: 14 }}>
          {profiles.map((p) => (
            <Card
              key={p.id}
              title={
                <span>
                  {p.name}{' '}
                  <Badge status={p.status === 'active' ? 'paid' : p.status === 'paused' ? 'partially_paid' : 'draft'}>
                    {p.status}
                  </Badge>{' '}
                  <Badge status={p.mode === 'auto_send' ? 'issued' : 'draft'}>
                    {p.mode === 'auto_send' ? 'Auto-send' : 'Draft only'}
                  </Badge>
                </span>
              }
              actions={
                <>
                  <button
                    className="btn sm"
                    onClick={async () => {
                      await api.patch(`/recurring/${p.id}`, {
                        status: p.status === 'active' ? 'paused' : 'active',
                      })
                      load()
                    }}
                  >
                    {p.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    className="btn sm primary"
                    onClick={async () => {
                      setError('')
                      try {
                        const r = await api.post<{ ok: boolean; invoiceId?: string; number?: string; mode?: string; error?: string; reason?: string }>(
                          `/recurring/${p.id}/run-now`,
                        )
                        if (r.ok) {
                          setNotice(
                            r.mode === 'sent'
                              ? `Generated and sent ${r.number}.`
                              : `Generated a draft${r.reason === 'over_approval_threshold' ? ' — the amount is above your approval threshold, so it was not sent.' : '.'}`,
                          )
                          if (r.invoiceId) navigate(`/invoices/${r.invoiceId}`)
                        } else setError(r.error ?? r.reason ?? 'Run failed')
                      } catch (e) {
                        setError(e instanceof ApiError ? e.message : 'Run failed')
                      }
                    }}
                  >
                    Run now
                  </button>
                  <button
                    className="btn sm danger"
                    onClick={async () => {
                      await api.del(`/recurring/${p.id}`)
                      load()
                    }}
                  >
                    Delete
                  </button>
                </>
              }
            >
              {p.consecutive_failures >= 2 && (
                <Alert kind="warn">
                  Paused automatically after {p.consecutive_failures} consecutive failures.
                  {p.last_error && <> Last error: {p.last_error}</>}
                </Alert>
              )}

              <div className="grid c4">
                <div>
                  <div className="k muted small">Client</div>
                  <div style={{ fontWeight: 600 }}>{p.client_display_name || p.client_legal_name}</div>
                </div>
                <div>
                  <div className="k muted small">Frequency</div>
                  <div>
                    {FREQ_LABELS[p.frequency]}
                    {p.interval_n > 1 && ` × ${p.interval_n}`}
                  </div>
                </div>
                <div>
                  <div className="k muted small">Next run</div>
                  <div style={{ fontWeight: 600 }}>{fmtDate(p.next_run_on)}</div>
                </div>
                <div>
                  <div className="k muted small">Generated so far</div>
                  <div>
                    {p.occurrences_done}
                    {p.max_occurrences ? ` of ${p.max_occurrences}` : ''}
                  </div>
                </div>
              </div>

              <div className="sep" />
              <div className="small muted">
                Next 6 runs: {p.upcoming.map((d) => fmtDate(d)).join(' · ') || 'schedule complete'}
                {p.approval_threshold !== null && (
                  <> · Amounts over {money(p.approval_threshold)} always stay as drafts</>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <RecurringForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            load()
          }}
        />
      )}
    </Page>
  )
}

function RecurringForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [clients, setClients] = useState<Client[]>([])
  const [name, setName] = useState('')
  const [clientId, setClientId] = useState('')
  const [frequency, setFrequency] = useState('monthly')
  const [intervalN, setIntervalN] = useState('1')
  const [dayOfMonth, setDayOfMonth] = useState('1')
  const [startDate, setStartDate] = useState(today())
  const [endDate, setEndDate] = useState('')
  const [maxOcc, setMaxOcc] = useState('')
  const [mode, setMode] = useState('draft')
  const [threshold, setThreshold] = useState('')
  const [terms, setTerms] = useState('15')
  const [leadDays, setLeadDays] = useState('0')
  const [desc, setDesc] = useState('')
  const [sac, setSac] = useState('')
  const [qty, setQty] = useState('1')
  const [rate, setRate] = useState('')
  const [taxRate, setTaxRate] = useState('18')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<{ clients: Client[] }>('/clients').then((r) => setClients(r.clients))
  }, [])

  const save = async () => {
    setError('')
    setBusy(true)
    try {
      await api.post('/recurring', {
        client_id: clientId,
        name,
        frequency,
        interval_n: Number(intervalN),
        day_of_month: ['monthly', 'quarterly', 'half_yearly', 'yearly'].includes(frequency)
          ? Number(dayOfMonth)
          : null,
        lead_days: Number(leadDays),
        payment_terms_days: Number(terms),
        start_date: startDate,
        end_date: endDate || null,
        max_occurrences: maxOcc ? Number(maxOcc) : null,
        mode,
        approval_threshold: threshold ? Number(threshold) : null,
        template: {
          lines: [
            {
              description: desc,
              sac_hsn: sac || null,
              qty: Number(qty),
              unit: 'NOS',
              rate_paise: toPaise(rate || '0'),
              tax_rate: Number(taxRate),
            },
          ],
        },
      })
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      wide
      title="New recurring profile"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Create profile'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      <div className="grid c2">
        <Field label="Profile name">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly retainer" autoFocus />
        </Field>
        <Field label="Client">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Select…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name || c.legal_name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid c3">
        <Field label="Frequency">
          <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
            {Object.entries(FREQ_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label={frequency === 'custom_days' ? 'Every N days' : 'Interval'}>
          <input type="number" value={intervalN} onChange={(e) => setIntervalN(e.target.value)} />
        </Field>
        {['monthly', 'quarterly', 'half_yearly', 'yearly'].includes(frequency) && (
          <Field label="Day of month" hint="Clamped to the last day in shorter months.">
            <input type="number" min="1" max="31" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} />
          </Field>
        )}
      </div>

      <div className="grid c3">
        <Field label="Start date">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="End date (optional)">
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="Max occurrences (optional)">
          <input type="number" value={maxOcc} onChange={(e) => setMaxOcc(e.target.value)} />
        </Field>
      </div>

      <div className="grid c3">
        <Field label="Mode" hint="Auto-send finalizes and emails without you touching it.">
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="draft">Generate a draft for review</option>
            <option value="auto_send">Generate, finalize and send</option>
          </select>
        </Field>
        <Field
          label="Approval threshold (₹)"
          hint="Anything above this stays a draft even in auto-send mode."
        >
          <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="No limit" />
        </Field>
        <Field label="Payment terms (days)">
          <input type="number" value={terms} onChange={(e) => setTerms(e.target.value)} />
        </Field>
      </div>

      <Field label="Generate this many days early" hint="Useful for raising a bill before the period starts.">
        <input type="number" value={leadDays} onChange={(e) => setLeadDays(e.target.value)} />
      </Field>

      <div className="sep" />
      <h3 style={{ marginBottom: 10 }}>Invoice line</h3>
      <Field label="Description">
        <input type="text" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Monthly services retainer" />
      </Field>
      <div className="grid c4">
        <Field label="SAC/HSN">
          <input type="text" className="mono" value={sac} onChange={(e) => setSac(e.target.value)} />
        </Field>
        <Field label="Qty">
          <input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="Rate (₹)">
          <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <Field label="GST %">
          <input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
        </Field>
      </div>
      <div className="alert info small">
        Line total: {money(Math.round(Number(qty || 0) * toPaise(rate || '0')))} plus {taxRate}% GST.
        {fromPaise(0) && ''}
      </div>
    </Modal>
  )
}
